import React, { useRef } from 'react';
import { X, Printer } from '@/shared/icons';
import { motion, AnimatePresence } from 'motion/react';
import { useReactToPrint } from 'react-to-print';
import { QRCodeSVG } from 'qrcode.react';

interface QRCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  guid?: string;
}

const QR_DISPLAY_SIZE = 400;

const QRCodeModal: React.FC<QRCodeModalProps> = ({ 
  isOpen, 
  onClose, 
  guid,
}) => {
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: 'Patient Virtual Check In - QR Code',
    pageStyle: `
      @page {
        size: A4;
        margin: 20mm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `,
  });

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black/50" 
            onClick={onClose} 
          />
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="bg-white flex flex-col items-center relative lg-plus shadow-[0px_8px_16px_0px_rgba(0,0,0,0.15)] z-10 w-[600px] max-w-[95vw] max-h-[95vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <div className="flex justify-end w-full px-4 pt-3">
              <motion.button 
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                whileHover={{ scale: 1.1, opacity: 0.75 }}
                whileTap={{ scale: 0.9 }}
                className="bg-transparent cursor-pointer flex items-center justify-center opacity-50 p-1 rounded transition-opacity"
                type="button"
              >
                <X className="w-4 h-4 text-black" />
              </motion.button>
            </div>

            {/* Printable Content */}
            <div ref={printRef} className="flex flex-col items-center w-full px-[50px] pb-[40px]">
              {/* Hello Health Logo */}
              <img 
                src="/images/img_logo_1.png" 
                alt="Hello Health" 
                className="h-10 object-contain mb-[30px]"
              />

              {/* QR Code */}
              <div className="relative w-[400px] h-[400px] flex items-center justify-center mb-[28px]">
                {guid ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                    className="size-full flex items-center justify-center"
                  >
                    <QRCodeSVG
                      value={`virtualcheckinguid:${guid}`}
                      size={QR_DISPLAY_SIZE}
                      level="M"
                      bgColor="#FFFFFF"
                      fgColor="#000000"
                      marginSize={0}
                    />
                  </motion.div>
                ) : (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center text-gray-500 text-sm px-4"
                  >
                    <p>Failed to load QR code</p>
                    <p className="text-xs mt-2">Please try again</p>
                  </motion.div>
                )}
              </div>

              {/* Heading */}
              <h2 className="font-bold text-lg text-black text-center mb-[24px]">
                Scan for Patient Virtual Check In
              </h2>

              {/* Instructions */}
              <ul className="w-full max-w-[420px] flex flex-col gap-[12px] mb-[32px]">
                <li className="flex items-start gap-2.5 text-[15px] text-ink-soft leading-[1.5]">
                  <span className="mt-[8px] w-[6px] h-[6px] rounded-full bg-hh-primary shrink-0" />
                  Open the Hello Health app on your phone or tablet.
                </li>
                <li className="flex items-start gap-2.5 text-[15px] text-ink-soft leading-[1.5]">
                  <span className="mt-[8px] w-[6px] h-[6px] rounded-full bg-hh-primary shrink-0" />
                  Tap the QR code icon.
                </li>
                <li className="flex items-start gap-2.5 text-[15px] text-ink-soft leading-[1.5]">
                  <span className="mt-[8px] w-[6px] h-[6px] rounded-full bg-hh-primary shrink-0" />
                  Point your device toward this screen to capture the QR code.
                </li>
              </ul>

              {/* Print Button */}
              <button
                onClick={() => handlePrint()}
                className="flex items-center gap-2 text-hh-primary text-[15px] font-medium cursor-pointer bg-transparent border-none hover:opacity-70 transition-opacity"
                type="button"
              >
                <Printer className="w-[18px] h-[18px]" />
                Print
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default QRCodeModal;
